// src/components/settings/Users.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { axiosInstance } from "../../utils/commonImports";

const EditIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L8 18l-4 1 1-4Z" />
  </svg>
);

const LockIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const UnlockIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 9.5-2" />
  </svg>
);

const KeyIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="7.5" cy="15.5" r="3.5" />
    <path d="m10 13 9-9" />
    <path d="m15 4 2 2" />
    <path d="m17 2 2 2" />
  </svg>
);

const DeleteIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 15H6L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
);

const SearchIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-4-4" />
  </svg>
);

const RefreshIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 11a8.1 8.1 0 0 0-15.5-2" />
    <path d="M4 5v4h4" />
    <path d="M4 13a8.1 8.1 0 0 0 15.5 2" />
    <path d="M20 19v-4h-4" />
  </svg>
);

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  username: "",
  password: "",
  confirmPassword: "",
  role_id: "",
  role: "",
  is_active: true,
  must_change_password: false,
};

const Users = ({ clientId }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  // `mountedRef` guards against calling setState after this
  // component has unmounted (same intent as the `mounted`
  // flag pattern used in Sidebar.jsx / HomePage.jsx, but as a
  // ref so the same loadUsers/loadRoles functions can be
  // reused by the Refresh button and after mutations, not
  // just on initial mount).
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ---------------------------------------------------------
  // LOAD USERS
  // ---------------------------------------------------------
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosInstance.get("/api/users");

      if (!mountedRef.current) return;

      const data = response.data;

      if (Array.isArray(data)) {
        setUsers(data);
      } else if (Array.isArray(data?.users)) {
        setUsers(data.users);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error("Load users error:", err);

      if (mountedRef.current) {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Unable to load users.",
        );
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // LOAD ROLES
  // ---------------------------------------------------------
  const loadRoles = async () => {
    try {
      const response = await axiosInstance.get("/api/users/roles");

      if (!mountedRef.current) return;

      const data = response.data;

      if (Array.isArray(data)) {
        setRoles(data);
      } else if (Array.isArray(data?.roles)) {
        setRoles(data.roles);
      } else {
        setRoles([]);
      }
    } catch (err) {
      console.error("Load roles error:", err);

      if (mountedRef.current) {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Unable to load roles.",
        );
      }
    }
  };

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  // ---------------------------------------------------------
  // FILTER USERS
  // ---------------------------------------------------------
  const filteredUsers = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    return users.filter((user) => {
      const matchesSearch =
        !searchText ||
        String(user.name || "")
          .toLowerCase()
          .includes(searchText) ||
        String(user.email || "")
          .toLowerCase()
          .includes(searchText) ||
        String(user.username || "")
          .toLowerCase()
          .includes(searchText) ||
        String(user.phone || "")
          .toLowerCase()
          .includes(searchText);

      const userRole = user.role || user.role_name || user.roleName || "";

      const matchesRole =
        !roleFilter ||
        String(user.role_id || "") === String(roleFilter) ||
        userRole === roleFilter;

      const matchesStatus =
        !statusFilter ||
        (statusFilter === "active" && user.is_active === true) ||
        (statusFilter === "inactive" && user.is_active === false);

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, search, roleFilter, statusFilter]);

  // ---------------------------------------------------------
  // FORM CHANGE
  // ---------------------------------------------------------
  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setError("");
  };

  // ---------------------------------------------------------
  // OPEN ADD USER
  // ---------------------------------------------------------
  const openAddUser = () => {
    setEditingUser(null);
    setForm({
      ...emptyForm,
      role_id: roles.length > 0 ? roles[0].role_id : "",
      role: roles.length > 0 ? roles[0].name || roles[0].role_name || "" : "",
    });

    setError("");
    setSuccess("");
    setShowPassword(false);
    setShowModal(true);
  };

  // ---------------------------------------------------------
  // OPEN EDIT USER
  // ---------------------------------------------------------
  const openEditUser = (user) => {
    setEditingUser(user);

    const userRoleId = user.role_id || user.roleId || "";

    const userRole = user.role || user.role_name || user.roleName || "";

    setForm({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      username: user.username || "",
      password: "",
      confirmPassword: "",
      role_id: userRoleId,
      role: userRole,
      is_active: typeof user.is_active === "boolean" ? user.is_active : true,
      must_change_password: user.must_change_password === true,
    });

    setError("");
    setSuccess("");
    setShowPassword(false);
    setShowModal(true);
  };

  // ---------------------------------------------------------
  // CLOSE MODAL
  // ---------------------------------------------------------
  const closeModal = () => {
    if (saving) return;

    setShowModal(false);
    setEditingUser(null);
    setForm(emptyForm);
    setError("");
  };

  // ---------------------------------------------------------
  // VALIDATE FORM
  // ---------------------------------------------------------
  const validateForm = () => {
    if (!form.name.trim()) {
      return "User name is required.";
    }

    if (!form.email.trim()) {
      return "Email is required.";
    }

    if (!form.username.trim()) {
      return "Username is required.";
    }

    if (!editingUser) {
      if (!form.password) {
        return "Password is required.";
      }

      if (form.password.length < 6) {
        return "Password must be at least 6 characters.";
      }

      if (form.password !== form.confirmPassword) {
        return "Password and Confirm Password do not match.";
      }
    }

    if (form.password || form.confirmPassword) {
      if (form.password.length < 6) {
        return "Password must be at least 6 characters.";
      }

      if (form.password !== form.confirmPassword) {
        return "Password and Confirm Password do not match.";
      }
    }

    if (!form.role_id && !form.role) {
      return "Please select a role.";
    }

    return "";
  };

  // ---------------------------------------------------------
  // SAVE USER
  // ---------------------------------------------------------
  const handleSave = async (e) => {
    e.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const selectedRole = roles.find(
        (role) => String(role.role_id) === String(form.role_id),
      );

      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        username: form.username.trim(),
        role_id: form.role_id || selectedRole?.role_id || null,
        role:
          selectedRole?.name || selectedRole?.role_name || form.role || null,
        is_active: form.is_active,
        must_change_password: form.must_change_password,
      };

      // Password is only sent when required/changed
      if (!editingUser) {
        payload.password = form.password;
      } else if (form.password) {
        payload.password = form.password;
      }

      if (editingUser) {
        await axiosInstance.put(
          `/api/users/${editingUser.user_id}`,
          payload,
        );
      } else {
        await axiosInstance.post("/api/users", payload);
      }

      setSuccess(
        editingUser
          ? "User updated successfully."
          : "User created successfully.",
      );

      await loadUsers();

      setTimeout(() => {
        setShowModal(false);
        setEditingUser(null);
        setForm(emptyForm);
        setSuccess("");
      }, 700);
    } catch (err) {
      console.error("Save user error:", err);

      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to save user.",
      );
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------
  // ACTIVATE / DEACTIVATE
  // ---------------------------------------------------------
  const toggleStatus = async (user) => {
    const newStatus = !user.is_active;

    const action = newStatus ? "activate" : "deactivate";

    const confirmed = window.confirm(
      `Are you sure you want to ${action} "${user.name}"?`,
    );

    if (!confirmed) return;

    try {
      setError("");

      await axiosInstance.put(`/api/users/${user.user_id}/status`, {
        is_active: newStatus,
      });

      setSuccess(
        `User ${newStatus ? "activated" : "deactivated"} successfully.`,
      );

      await loadUsers();

      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      console.error("Status update error:", err);

      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to update user status.",
      );
    }
  };

  // ---------------------------------------------------------
  // RESET PASSWORD
  // ---------------------------------------------------------
  const resetPassword = async (user) => {
    const newPassword = window.prompt(`Enter a new password for ${user.name}:`);

    if (newPassword === null) return;

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    try {
      setError("");

      await axiosInstance.put(`/api/users/${user.user_id}/password`, {
        password: newPassword,
        must_change_password: true,
      });

      setSuccess("Password reset successfully.");

      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      console.error("Reset password error:", err);

      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to reset password.",
      );
    }
  };

  // ---------------------------------------------------------
  // DELETE USER
  // ---------------------------------------------------------
  const deleteUser = async (user) => {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete "${user.name}"?`,
    );

    if (!confirmed) return;

    try {
      setError("");

      await axiosInstance.delete(`/api/users/${user.user_id}`);

      setSuccess("User deleted successfully.");

      await loadUsers();

      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      console.error("Delete user error:", err);

      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to delete user.",
      );
    }
  };

  // ---------------------------------------------------------
  // FORMAT LAST LOGIN
  // ---------------------------------------------------------
  const formatLastLogin = (value) => {
    if (!value) return "Never";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ---------------------------------------------------------
  // ROLE NAME
  // ---------------------------------------------------------
  const getRoleName = (user) => {
    if (user.role_name) return user.role_name;
    if (user.roleName) return user.roleName;
    if (user.role) return user.role;

    const role = roles.find(
      (item) => String(item.role_id) === String(user.role_id),
    );

    return role?.name || role?.role_name || "—";
  };

  return (
    <div className="w-full h-full bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* =====================================================
          HEADER
      ====================================================== */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-800">Users</h2>

          <p className="text-xs text-gray-500 mt-0.5">
            Manage users, roles and account access.
          </p>
        </div>

        <button
          type="button"
          onClick={openAddUser}
          className="h-8 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md flex items-center gap-1.5"
        >
          <span className="text-sm">+</span>
          Add User
        </button>
      </div>

      {/* =====================================================
          MESSAGES
      ====================================================== */}
      {error && (
        <div className="mx-4 mt-3 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-700 flex items-center justify-between">
          <span>{error}</span>

          <button
            type="button"
            onClick={() => setError("")}
            className="ml-3 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {success && (
        <div className="mx-4 mt-3 px-3 py-2 rounded-md bg-green-50 border border-green-200 text-xs text-green-700">
          {success}
        </div>
      )}

      {/* =====================================================
    FILTER BAR
====================================================== */}
      <div className="px-4 py-2.5 border-b border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(260px,1fr)_170px_150px_auto] gap-2">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, username or mobile..."
              className="w-full h-8 pl-8 pr-3 border border-gray-300 rounded-md text-xs text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />

            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
              <SearchIcon />
            </span>
          </div>

          {/* Role */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="h-8 px-2.5 border border-gray-300 rounded-md text-xs text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All Roles</option>

            {roles.map((role) => (
              <option key={role.role_id} value={role.role_id}>
                {role.name || role.role_name}
              </option>
            ))}
          </select>

          {/* Status */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-8 px-2.5 border border-gray-300 rounded-md text-xs text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Refresh */}
          <button
            type="button"
            onClick={loadUsers}
            disabled={loading}
            title="Refresh Users"
            className="h-8 px-3 border border-gray-300 rounded-md text-xs text-gray-600 bg-white hover:bg-gray-50 flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <RefreshIcon />
            Refresh
          </button>
        </div>
      </div>

      {/* =====================================================
          SUMMARY
      ====================================================== */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          Showing{" "}
          <span className="font-medium text-gray-700">
            {filteredUsers.length}
          </span>{" "}
          of <span className="font-medium text-gray-700">{users.length}</span>{" "}
          users
        </span>

        <span className="text-xs text-gray-400">
          Client ID: {clientId || "—"}
        </span>
      </div>

      {/* =====================================================
          TABLE

          NOTE: <colgroup> must be a direct child of <table>,
          placed before <thead>. It was previously rendered as
          a sibling <div> after the closing </table> tag, which
          is invalid placement — browsers silently ignore it,
          so the column-width classes had no effect. Moved
          inside <table> below.
      ====================================================== */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[950px] text-xs">
          <colgroup>
            <col className="w-[21%]" />
            <col className="w-[23%]" />
            <col className="w-[12%]" />
            <col className="w-[12%]" />
            <col className="w-[10%]" />
            <col className="w-[13%]" />
            <col className="w-[9%]" />
          </colgroup>

          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-gray-500">
              <th className="text-left font-medium px-4 py-2.5">User Name</th>

              <th className="text-left font-medium px-3 py-2.5">Email</th>

              <th className="text-left font-medium px-3 py-2.5">Mobile</th>

              <th className="text-left font-medium px-3 py-2.5">Role</th>

              <th className="text-center font-medium px-3 py-2.5">Status</th>

              <th className="text-left font-medium px-3 py-2.5">Last Login</th>

              <th className="text-center font-medium px-3 py-2.5 w-[170px]">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center py-10 text-gray-400">
                  Loading users...
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-10">
                  <div className="text-gray-400 text-sm">No users found</div>

                  <div className="text-gray-400 text-xs mt-1">
                    Try changing your search or filters.
                  </div>
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr
                  key={user.user_id}
                  className="border-b border-gray-100 hover:bg-blue-50/30"
                >
                  {/* User */}
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center font-semibold text-[11px] shrink-0">
                        {String(user.name || "?")
                          .charAt(0)
                          .toUpperCase()}
                      </div>

                      <div className="min-w-0">
                        <div
                          className="font-medium text-gray-800 truncate"
                          title={user.name}
                        >
                          {user.name || "—"}
                        </div>

                        {user.username && (
                          <div
                            className="text-[10px] text-gray-400 truncate"
                            title={user.username}
                          >
                            @{user.username}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Email */}
                  <td className="px-3 py-2.5 text-gray-600">
                    {user.email || "—"}
                  </td>

                  {/* Phone */}
                  <td className="px-3 py-2.5 text-gray-600">
                    {user.phone || "—"}
                  </td>

                  {/* Role */}
                  <td className="px-3 py-2.5">
                    <span className="inline-flex px-2 py-1 rounded bg-gray-100 text-gray-600">
                      {getRoleName(user)}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="px-3 py-2.5 text-center">
                    {user.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 text-green-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 text-red-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                        Inactive
                      </span>
                    )}
                  </td>

                  {/* Last Login */}
                  <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">
                    {formatLastLogin(user.last_login)}
                  </td>

                  {/* Actions

                      Now uses the SVG icon components defined
                      above (EditIcon / LockIcon / UnlockIcon /
                      KeyIcon / DeleteIcon) instead of raw emoji,
                      each with an aria-label for screen readers.
                  */}
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEditUser(user)}
                        className="px-2 py-1.5 rounded text-blue-600 hover:bg-blue-50"
                        title="Edit User"
                        aria-label={`Edit ${user.name || "user"}`}
                      >
                        <EditIcon />
                      </button>

                      <button
                        type="button"
                        onClick={() => toggleStatus(user)}
                        className={`px-2 py-1.5 rounded ${
                          user.is_active
                            ? "text-orange-600 hover:bg-orange-50"
                            : "text-green-600 hover:bg-green-50"
                        }`}
                        title={user.is_active ? "Deactivate" : "Activate"}
                        aria-label={
                          user.is_active
                            ? `Deactivate ${user.name || "user"}`
                            : `Activate ${user.name || "user"}`
                        }
                      >
                        {user.is_active ? <LockIcon /> : <UnlockIcon />}
                      </button>

                      <button
                        type="button"
                        onClick={() => resetPassword(user)}
                        className="px-2 py-1.5 rounded text-purple-600 hover:bg-purple-50"
                        title="Reset Password"
                        aria-label={`Reset password for ${user.name || "user"}`}
                      >
                        <KeyIcon />
                      </button>

                      <button
                        type="button"
                        onClick={() => deleteUser(user)}
                        className="px-2 py-1.5 rounded text-red-600 hover:bg-red-50"
                        title="Delete User"
                        aria-label={`Delete ${user.name || "user"}`}
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* =====================================================
          ADD / EDIT USER MODAL
      ====================================================== */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl bg-white rounded-lg shadow-xl overflow-hidden">
            {/* Modal Header */}
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">
                  {editingUser ? "Edit User" : "Add User"}
                </h3>

                <p className="text-[11px] text-gray-500 mt-0.5">
                  {editingUser
                    ? "Update user details and access."
                    : "Create a new user account."}
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="w-7 h-7 rounded hover:bg-gray-100 text-gray-500 text-lg"
              >
                ×
              </button>
            </div>

            {/* Modal Error */}
            {error && (
              <div className="mx-5 mt-3 px-3 py-2 rounded bg-red-50 border border-red-200 text-xs text-red-700">
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSave}>
              <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Enter user name"
                      className="w-full h-8 px-2.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="user@example.com"
                      className="w-full h-8 px-2.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  {/* Mobile */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Mobile
                    </label>

                    <input
                      type="text"
                      value={form.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="Mobile number"
                      maxLength={20}
                      className="w-full h-8 px-2.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  {/* Username */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Username <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="text"
                      value={form.username}
                      onChange={(e) => handleChange("username", e.target.value)}
                      placeholder="Login username"
                      maxLength={50}
                      className="w-full h-8 px-2.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {editingUser ? "New Password" : "Password"}{" "}
                      {!editingUser && <span className="text-red-500">*</span>}
                    </label>

                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={form.password}
                        onChange={(e) =>
                          handleChange("password", e.target.value)
                        }
                        placeholder={
                          editingUser
                            ? "Leave blank to keep current"
                            : "Minimum 6 characters"
                        }
                        className="w-full h-8 px-2.5 pr-12 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />

                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500"
                      >
                        {showPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Confirm Password{" "}
                      {!editingUser && <span className="text-red-500">*</span>}
                    </label>

                    <input
                      type={showPassword ? "text" : "password"}
                      value={form.confirmPassword}
                      onChange={(e) =>
                        handleChange("confirmPassword", e.target.value)
                      }
                      placeholder="Confirm password"
                      className="w-full h-8 px-2.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  {/* Role */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Role <span className="text-red-500">*</span>
                    </label>

                    <select
                      value={form.role_id}
                      onChange={(e) => {
                        const selectedRoleId = e.target.value;

                        const selected = roles.find(
                          (role) =>
                            String(role.role_id) === String(selectedRoleId),
                        );

                        // Single state update — role_id and its
                        // matching role name are set together.
                        setForm((prev) => ({
                          ...prev,
                          role_id: selectedRoleId,
                          role: selected?.name || selected?.role_name || "",
                        }));

                        setError("");
                      }}
                      className="w-full h-8 px-2 border border-gray-300 rounded-md text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select Role</option>

                      {roles.map((role) => (
                        <option key={role.role_id} value={role.role_id}>
                          {role.name || role.role_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Status
                    </label>

                    <select
                      value={form.is_active ? "active" : "inactive"}
                      onChange={(e) =>
                        handleChange("is_active", e.target.value === "active")
                      }
                      className="w-full h-8 px-2 border border-gray-300 rounded-md text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="active">Active</option>

                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  {/* Force Password Change */}
                  <div className="md:col-span-2 pt-1">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.must_change_password}
                        onChange={(e) =>
                          handleChange("must_change_password", e.target.checked)
                        }
                        className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />

                      <span className="text-xs text-gray-600">
                        Force user to change password on next login
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="h-8 px-4 border border-gray-300 rounded-md text-xs text-gray-600 hover:bg-white disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="h-8 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium disabled:opacity-50"
                >
                  {saving
                    ? "Saving..."
                    : editingUser
                      ? "Update User"
                      : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
